import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTextArea, RCCSwitch } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Audit from "src/components/Audit";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import Title from "src/components/Title";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import Select from "src/components/Select";
import addAndChangeSchema from "../schemas";
import { IPresentationgroupItem, IRefPresentationModal } from "../interface";
import { IndicatorsGroupers } from "../../Panel/interface";

const PresentationGroupModal: React.ForwardRefRenderFunction<IRefPresentationModal> = (_, ref) => {
	const { translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const [onCloseFunction,setOnClosefunction] = useState<((presentationGroup?:IPresentationgroupItem)=>void)>();
	const [selectedPresentation, setSelectedPresentation] = useState<IPresentationgroupItem>({} as IPresentationgroupItem);
	const controllerUrl = "PresentationGroups/";

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const actionButtonDelete = (row: IPresentationgroupItem, onClose?:()=>void) => {
		confirm(
			translate("Exclusão do grupo de apresentação"),
			`${translate("Deseja realmente excluir o grupo")}: ${row.iqa001Nome} ?`,
			async () => {
				
				await RCCServicesApi.deleteRequest({
					method: `${controllerUrl}Delete`,
					urlParameters: {
						id: row.iqa001Id
					}
				});

				addToast({
					color: "green",
					description: translate("Grupo de apresentação excluído com sucesso"),
					title: translate("Sucesso")
				});

				if(onClose)
					onClose();

			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir grupo de apresentação"), addToast);
			},
			true
		);
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange ,
		control: controlAddOrChange
	} = useForm();

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			await addAndChangeSchema(translate).validate(data, {
				abortEarly: false
			});
			
			if(isAdd){
				await RCCServicesApi.postRequest({
					method: `${controllerUrl}Insert`,
					body: {
						...data,
						iqa001Agrupador: Number(data.iqa001Agrupador)
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: `${controllerUrl}Update`,
					body: {
						...data,
						iqa001Id: selectedPresentation.iqa001Id,
						iqa001Agrupador: Number(data.iqa001Agrupador)
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Grupo de apresentação adicionado com sucesso") : translate("Grupo de apresentação alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			if(onCloseFunction)
				onCloseFunction();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar grupo de apresentação") : translate("Erro ao alterar grupo de apresentação"), addToast);
		}
	};
	
	const actionButtonAdd = (onClose?:(presentationGroup?:IPresentationgroupItem)=>void) => {
		resetAddOrChange(); 
		setSelectedPresentation({iqa001Ativo:true, iqa001Agrupador: 6} as IPresentationgroupItem); 
		setOnClosefunction(() => onClose);
		setIsAdd(true); 
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (row: IPresentationgroupItem,onClose?:(presentationGroup?:IPresentationgroupItem)=>void) => {
		resetAddOrChange(); 
		setSelectedPresentation(row); 
		setOnClosefunction(() => onClose);
		setIsAdd(false); 
		modalAddOrChange.current?.openModal(); 
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    
	return (
		<>	
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar grupo de apresentação") : translate("Alterar grupo de apresentação"),
					icon: {
						name: "document"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						alignItems="center"
						justify="space-between"
					>
						<Title color="purple" size="md">
						    {translate("Dados Gerais")}
						</Title>
						<Audit data={selectedPresentation} type="pop"/>
						<hr />
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="iqa001Nome"
							label={translate("Nome")}
							maxLength={60}
							defaultValue={selectedPresentation.iqa001Nome}
							error={errorsAddOrChange.iqa001Nome}
							required
						/> 
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCTextArea
							register={registerAddOrChange}
							name="iqa001Descricao"
							label={translate("Descrição")}
							maxLength={600}
							defaultValue={selectedPresentation.iqa001Descricao}
							error={errorsAddOrChange.iqa001Descricao}
							required
						/> 
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							lg={3}
						>
							<RCCSwitch 
								name="iqa001Ativo" 
								label = {translate("Ativo")} 
								register = {registerAddOrChange} 
								defaultChecked = {selectedPresentation.iqa001Ativo}
							/> 
						</RCCGrid>
					</RCCGrid>
					<RCCGrid 
						container 
						item 
						xs={12}
						sm={10}
					>
						<Select 
							control={controlAddOrChange}
							name="iqa001Agrupador"
							label={translate("Agrupadores")}
							options={[
								{ value: IndicatorsGroupers.SECTOR, label: `${translate("Setor")}` },
								{ value: IndicatorsGroupers.SECTOR_RESPONSIBLE, label: `${translate("Setor")} -> ${translate("Responsável")}` },
								{ value: IndicatorsGroupers.MAP, label: `${translate("Mapa")}` },
								{ value: IndicatorsGroupers.MAP_PERSPECTIVE, label: `${translate("Mapa")} -> ${translate("Perspectiva")}` },
								{ value: IndicatorsGroupers.MAP_PERSPECTIVE_OBJECTIVE, label: `${translate("Mapa")} -> ${translate("Perspectiva")} -> ${translate("Objetivo")}` },
								{ value: IndicatorsGroupers.PRESENTATION_GROUP, label: `${translate("Grupo de apresentação")}` },
								{ value: IndicatorsGroupers.INDICATOR_CLASSIFICATION, label: `${translate("Classificação do indicador")}` },
								{ value: IndicatorsGroupers.BUDGET_PLAN, label: `${translate("Plano orçamentário")}` }
							]}
							defaultValue={selectedPresentation.iqa001Agrupador !== undefined ? selectedPresentation.iqa001Agrupador.toString() : "6"}
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(PresentationGroupModal);

